import { 
  MapPin, 
  Cpu, 
  BarChart3, 
  Monitor, 
  Bell, 
  Leaf,
  Users,
  Smartphone,
  ShoppingBag,
  CheckCircle,
  ArrowRight
} from 'lucide-react'
import { Link } from 'react-router-dom'

const serviceCategories = [
  {
    id: 'planning',
    icon: MapPin,
    title: '投建规划',
    subtitle: '科学选址 · 精准投资 · 高效落地',
    description: '基于大数据分析和行业经验，为充电站投资提供全周期规划服务，降低投资风险，提升投资回报率。',
    color: 'from-neon-cyan to-neon-blue',
    bgColor: 'bg-neon-cyan/10',
    features: [
      {
        icon: MapPin,
        title: '选址分析',
        description: '基于高德地图大数据，结合交通流量、周边配套、竞争分布等多维度数据，提供科学的选址建议。',
      },
      {
        icon: Cpu,
        title: '设备选型',
        description: '根据场地条件、预算规模、目标客群，提供定制化的充电设备选型方案。',
      },
      {
        icon: BarChart3,
        title: '投资回报模型',
        description: '建立精准的投资回报测算模型，综合考虑建设成本、运营成本、收入预测等因素。',
      },
    ],
    benefits: [
      '大数据支撑科学决策',
      '定制化设备配置方案',
      '精准的投资回报预测',
      '全流程投建指导',
    ],
  },
  {
    id: 'operation',
    icon: Monitor,
    title: '智能运维',
    subtitle: '实时监控 · 智能预警 · 能效优化',
    description: '通过物联网技术和智能算法，实现充电设备的远程监控、故障预警和能效优化，降低运营成本。',
    color: 'from-neon-green to-emerald-400',
    bgColor: 'bg-neon-green/10',
    features: [
      {
        icon: Monitor,
        title: '远程监控',
        description: '7×24小时实时监控充电设备运行状态，包括电压、电流、温度等关键参数。',
      },
      {
        icon: Bell,
        title: '故障预警',
        description: '基于AI算法的智能故障预测系统，提前发现潜在问题，减少设备停机时间。',
      },
      {
        icon: Leaf,
        title: '能效优化',
        description: '智能调度充电功率，优化能源使用效率，降低运营成本。',
      },
    ],
    benefits: [
      '7×24小时实时监控',
      'AI智能故障预测',
      '能效优化降低成本',
      '快速响应处理',
    ],
  },
  {
    id: 'branding',
    icon: Users,
    title: '品牌运营',
    subtitle: '流量导入 · 用户运营 · 增值服务',
    description: '整合多渠道流量资源，提供用户运营工具和增值服务，帮助运营商提升品牌影响力和收益。',
    color: 'from-neon-purple to-violet-400',
    bgColor: 'bg-neon-purple/10',
    features: [
      {
        icon: Smartphone,
        title: '流量接入',
        description: '对接高德地图、支付宝、腾讯等超级APP，为充电站导入海量用户流量。',
      },
      {
        icon: Users,
        title: '用户运营',
        description: '提供会员管理、营销活动、积分系统等工具，提升用户粘性和复购率。',
      },
      {
        icon: ShoppingBag,
        title: '增值服务',
        description: '拓展车后服务、广告投放等增值业务，增加充电站的多元化收入来源。',
      },
    ],
    benefits: [
      '多渠道流量导入',
      '完善的用户运营体系',
      '多元化增值业务',
      '品牌影响力提升',
    ],
  },
]

export default function Services() {
  return (
    <div className="overflow-hidden bg-dark">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0">
          <div className="absolute inset-0 radial-glow opacity-50" />
          <div className="absolute inset-0 grid-pattern opacity-30" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-6">
              <span className="w-2 h-2 rounded-full bg-neon-green animate-pulse" />
              <span className="text-neon-green text-sm font-medium">核心服务</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              三大服务板块<span className="text-gradient">，全方位赋能</span>
            </h1>
            <p className="text-lg text-white/50 leading-relaxed">
              从投建规划到智能运维，再到品牌运营，我们为充电运营商提供全生命周期的专业服务，助力您的充电业务高效运营
            </p>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20 md:space-y-32">
            {serviceCategories.map((category) => (
              <div key={category.id} className="relative">
                {/* Section Header */}
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12">
                  <div>
                    <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full ${category.bgColor} mb-4`}>
                      <category.icon className={`w-4 h-4 text-transparent bg-clip-text bg-gradient-to-r ${category.color}`} />
                      <span className={`text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r ${category.color}`}>
                        {category.title}
                      </span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">{category.title}</h2>
                    <p className="text-neon-green font-medium">{category.subtitle}</p>
                  </div>
                  <p className="text-white/50 max-w-xl mt-4 lg:mt-0 lg:text-right">
                    {category.description}
                  </p>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {category.features.map((feature) => (
                    <div
                      key={feature.title}
                      className="glass-card p-6 hover:border-neon-green/30 transition-all duration-300 group"
                    >
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform neon-glow`}>
                        <feature.icon className="w-6 h-6 text-dark" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                      <p className="text-white/50 text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  ))}
                </div>

                {/* Benefits */}
                <div className="mt-8 glass-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 text-neon-green mr-2" />
                    服务优势
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {category.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${category.color}`} />
                        <span className="text-white/70 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 relative">
        <div className="absolute inset-0 radial-glow opacity-50" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            需要定制化服务方案？
          </h2>
          <p className="text-white/50 text-lg mb-10 max-w-2xl mx-auto">
            我们的专家团队将根据您的具体需求，为您量身定制最适合的服务组合
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-neon-green text-dark rounded-xl font-semibold hover:bg-neon-green/90 transition-all hover:scale-105 neon-glow-strong"
            >
              咨询定制方案
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
